import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";

import {
  ContentCategoryRow,
  ContentDifficulty,
  ContentQuestionRow,
} from "../../common/contentTypes";
import * as Content from "../../services/api/Content";
import { CategorySelect } from "./CategorySelect";
import {
  ContentLintIssue,
  MAX_CHOICE_LABEL,
  QuestionFormState,
  choiceCounterState,
  emptyQuestionForm,
  unresolvedIssue,
  validateQuestionForm,
} from "./questionForm";

interface Props {
  /** Absent when creating. */
  question?: ContentQuestionRow;
  categories: ContentCategoryRow[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: (saved: ContentQuestionRow) => void;
  /** Opens category creation without losing the edit in progress. */
  onProposeCategory?: () => void;
  /**
   * Takedown from inside the editor: the moment you realise a question is
   * wrong is while you are reading it, not back in the table.
   */
  onFlagAndRemove?: (question: ContentQuestionRow) => void;
  /**
   * The Quality bucket this question was opened from. Set only by that screen,
   * and it makes the fix mandatory rather than advisory.
   */
  issue?: ContentLintIssue;
}

const DIFFICULTIES: ContentDifficulty[] = ["easy", "medium", "hard"];

/** Rubric codes mean nothing to a reviewer, so say what to do instead. */
const qaMessage = (message: string) => {
  if (!message.includes("failed required QA checks")) {
    return message;
  }
  if (message.includes("C13")) {
    return "The profanity check still matches this text, so it cannot be saved. If that is a false positive, dismiss the flag instead of fixing it.";
  }
  if (message.includes("C7")) {
    return "A question needs exactly four choices.";
  }
  if (message.includes("C11")) {
    return "Exactly one choice must be marked correct.";
  }
  return message;
};

/** Red once the label cannot be served, amber while it is close to the cap. */
const counterTone = ({ over, near }: { over: boolean; near: boolean }) => {
  if (over) {
    return "text-danger";
  }
  if (near) {
    return "text-warning";
  }
  return "text-muted";
};

const formFromQuestion = (
  question: ContentQuestionRow | undefined,
  fallbackCategoryId: string,
): QuestionFormState =>
  question
    ? {
        title: question.title,
        categoryId: question.categoryId,
        difficulty: question.difficulty,
        choices: question.choices.map((choice) => ({
          label: choice.label,
          correct: choice.correct,
        })),
        hiddenOnPlatforms: question.hiddenOnPlatforms ?? [],
      }
    : emptyQuestionForm(fallbackCategoryId);

export const QuestionEditorModal: React.FC<Props> = ({
  question,
  categories,
  isOpen,
  onClose,
  onSaved,
  onProposeCategory,
  onFlagAndRemove,
  issue,
}) => {
  const [form, setForm] = useState<QuestionFormState>(() =>
    formFromQuestion(question, categories[0]?.id ?? ""),
  );
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    setForm(formFromQuestion(question, categories[0]?.id ?? ""));
    setAttempted(false);
  }, [question, categories]);

  const { errors, warnings } = validateQuestionForm(form);
  /**
   * Only set when the editor was opened from a Quality row: the issue that row
   * was listed for, if it is still there. It blocks the save, because saving
   * from Quality without fixing it just puts the row back.
   */
  const blockingIssue = unresolvedIssue(form, issue, question);

  /** Which choices collide, so the inputs themselves can say so. */
  const duplicateChoiceIndexes = new Set(
    form.choices
      .map((choice, index) => {
        const label = choice.label.trim().toLowerCase();
        const collides =
          label.length > 0 &&
          form.choices.some(
            (other, otherIndex) =>
              otherIndex !== index &&
              other.label.trim().toLowerCase() === label,
          );
        return collides ? index : -1;
      })
      .filter((index) => index >= 0),
  );

  const setChoice = (index: number, label: string) =>
    setForm((prev) => ({
      ...prev,
      choices: prev.choices.map((choice, i) =>
        i === index ? { ...choice, label } : choice,
      ),
    }));

  const setCorrect = (index: number) =>
    setForm((prev) => ({
      ...prev,
      choices: prev.choices.map((choice, i) => ({
        ...choice,
        correct: i === index,
      })),
    }));

  const save = async () => {
    if (errors.length > 0 || blockingIssue) {
      setAttempted(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        categoryId: form.categoryId,
        difficulty: form.difficulty,
        choices: form.choices.map((choice) => ({
          label: choice.label.trim(),
          correct: choice.correct,
        })),
      };
      const saved = question
        ? await Content.updateQuestion({
            questionId: question.id,
            ...payload,
          })
        : await Content.createQuestion(payload);
      toast.success(question ? "Question updated" : "Question created");
      onSaved(saved);
    } catch (err) {
      toast.error(qaMessage((err as Error).message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {question ? "Edit Question" : "Add Question"}
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="content-question-title">Question title</Label>
            <Input
              id="content-question-title"
              placeholder="e.g. Which country won the 2018 FIFA World Cup?"
              value={form.title}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, title: e.target.value }))
              }
            />
          </FormGroup>

          <FormGroup>
            <Label>
              Answer choices{" "}
              <span className="text-muted">(select the correct one)</span>
            </Label>
            {form.choices.map((choice, index) => {
              const counter = choiceCounterState(choice.label);
              const duplicate = duplicateChoiceIndexes.has(index);
              return (
                <div key={index} className="mb-2">
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <Input
                          addon
                          type="radio"
                          checked={choice.correct}
                          onChange={() => setCorrect(index)}
                          title="Mark as the correct answer"
                        />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder={`Choice ${index + 1}`}
                      value={choice.label}
                      // Marks the offending field itself, so a report of "one
                      // label is too long" does not send you counting.
                      invalid={counter.over || duplicate}
                      onChange={(e) => setChoice(index, e.target.value)}
                    />
                  </InputGroup>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">
                      {choice.correct ? "correct answer" : ""}
                    </small>
                    <small className={counterTone(counter)}>
                      {counter.count}/{MAX_CHOICE_LABEL}
                      {counter.over && (
                        <>
                          {" "}
                          <i className="fas fa-exclamation-triangle ml-1" />{" "}
                          exceeds Slack render limit — question will never be
                          served
                        </>
                      )}
                      {!counter.over && duplicate && (
                        <span className="text-danger">
                          {" "}
                          <i className="fas fa-exclamation-triangle ml-1" />{" "}
                          duplicate of another choice
                        </span>
                      )}
                    </small>
                  </div>
                </div>
              );
            })}
          </FormGroup>

          <Row form>
            <Col md={8}>
              <FormGroup>
                <Label for="content-question-category">Category</Label>
                <CategorySelect
                  id="content-question-category"
                  categories={categories}
                  value={form.categoryId}
                  onChange={(categoryId) =>
                    setForm((prev) => ({ ...prev, categoryId }))
                  }
                  placeholder="Select a category…"
                />
                {onProposeCategory && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={onProposeCategory}
                  >
                    <i className="fas fa-plus mr-1" />
                    Propose new category
                  </Button>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="content-question-difficulty">Difficulty</Label>
                <Input
                  id="content-question-difficulty"
                  type="select"
                  className="custom-select"
                  value={form.difficulty}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      difficulty: e.target.value as ContentDifficulty,
                    }))
                  }
                >
                  {DIFFICULTIES.map((difficulty) => (
                    <option key={difficulty} value={difficulty}>
                      {difficulty}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            {/* Deleted is not offered: takedowns are undone from the flag
                queue, which records who restored it and why. */}
          </Row>

          {attempted && errors.length > 0 && (
            <Alert color="danger" className="mb-2">
              {errors.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </Alert>
          )}
          {blockingIssue && (
            <Alert color={attempted ? "danger" : "warning"} className="mb-2">
              <i className="fas fa-exclamation-triangle mr-1" />
              {blockingIssue}
            </Alert>
          )}
          {/*
           * Suppressed while an issue is blocking: the same over-length label
           * would otherwise be reported twice, once as advice and once as a
           * blocker.
           */}
          {!blockingIssue && warnings.length > 0 && (
            <Alert color="warning" className="mb-0">
              {warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
              <small className="text-muted">
                Warnings do not block saving — they show up in the Quality
                report.
              </small>
            </Alert>
          )}
        </Form>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <div>
          {question && onFlagAndRemove && (
            <Button
              color="link"
              className="text-danger p-0"
              disabled={saving}
              onClick={() => onFlagAndRemove(question)}
            >
              <i className="fas fa-flag mr-1" />
              Flag &amp; remove this question
            </Button>
          )}
        </div>
        <div>
          <Button color="light" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            color="primary"
            className="ml-2"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : question ? "Save changes" : "Add question"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
